import { HStack, VStack } from '@chakra-ui/react';
import { useUser } from '@clerk/nextjs';
import berriesSrc from '@public/images/berries.png';
import berrySrc from '@public/images/berry.png';
import leafSrc from '@public/images/leaf.png';
import orangeSrc from '@public/images/orange.png';
import { MENU_WIDTH } from '@web-app/components/consts';
import Header from '@web-app/components/Header';
import { EVENT_NAMES, useAnalytics } from '@web-app/context/AnalyticsContext';
import {
  OrganizationContext,
  OrganizationContextProps,
} from '@web-app/hocs/organization';
import { useEnsureProviderAuth } from '@web-app/hooks/useEnsureProviderAuth';
import { useIntercom } from '@web-app/hooks/useIntercom';
import { useOrganizationConfig } from '@web-app/hooks/whitelabel/useOrganizationConfig';
import styles from '@web-app/styles/mobile.module.css';
import { FC, ReactNode, useContext, useEffect, useMemo, useRef } from 'react';

interface LayoutProps {
  children: ReactNode;
  hideIllustrations?: boolean;
  disableIntercom?: boolean;
}

const Layout: FC<LayoutProps> = ({
  children,
  hideIllustrations = false,
  disableIntercom = false,
}) => {
  const { organizationId } =
    useContext<OrganizationContextProps>(OrganizationContext);
  const config = useOrganizationConfig();
  const { user } = useUser();
  const { trackEvent } = useAnalytics();

  const { initializeIntercom, shutdownIntercom } = useIntercom();

  useEffect(() => {
    if (disableIntercom) {
      shutdownIntercom();
      return;
    }

    initializeIntercom();

    return () => {
      shutdownIntercom();
    };
  }, [disableIntercom, user, initializeIntercom, shutdownIntercom]);

  const refUserLoggedIn = useRef(!!user);

  const { error } = useEnsureProviderAuth({ redirectTo: '/' });

  useEffect(() => {
    if (refUserLoggedIn.current && !user) {
      trackEvent(EVENT_NAMES.LOGOUT);
    }

    if (
      user?.lastSignInAt &&
      Date.now() - new Date(user.lastSignInAt).getTime() < 5000
    ) {
      trackEvent(EVENT_NAMES.SIGN_IN);
    }

    refUserLoggedIn.current = !!user;
  }, [user, trackEvent]);

  const showSidebar = !!user;

  const backgroundStyles = useMemo(() => {
    if (!organizationId || !config) {
      return 'var(--bg-color-light)';
    }

    return !config.app.showBackgroundIllustrations || hideIllustrations
      ? 'var(--bg-color-light)'
      : [
          `
        url(${leafSrc.src}) right 25% top 100% no-repeat,
        url(${berriesSrc.src}) right 75% bottom -4rem no-repeat,
        url(${orangeSrc.src}) right -30% top 75% no-repeat,
        url(${berrySrc.src}) left 0% top 13% no-repeat,
        var(--bg-color-light)`,
          `
        url(${leafSrc.src}) right 5rem top 100% no-repeat,
        url(${berriesSrc.src}) right 13rem bottom -5% no-repeat,
        url(${orangeSrc.src}) right -30% top 75% no-repeat,
        url(${berrySrc.src}) left 0% top 13% no-repeat,
        var(--bg-color-light)`,
          `
        url(${leafSrc.src}) right 5rem top 100% no-repeat,
        url(${berriesSrc.src}) right 13rem bottom -3% no-repeat,
        url(${orangeSrc.src}) right 10% top 20% no-repeat,
        url(${berrySrc.src}) left 0% top 30% no-repeat,
        var(--bg-color-light)`,
        ];
  }, [config, hideIllustrations, organizationId]);

  // not authorized
  if (error) return null;

  return (
    <VStack
      overflow="auto"
      bg={backgroundStyles}
      backgroundSize={[
        'auto, auto, 40%, auto',
        'auto, auto, 40%, auto',
        'auto, auto, auto, auto',
        'auto, auto, auto, auto',
      ]}
      justifyContent="center"
      className={styles.minHeightFullViewportHeight}
    >
      {!showSidebar && <Header logoColor="dark" />}

      <HStack w="100%" paddingLeft={showSidebar ? MENU_WIDTH : 0}>
        <VStack flexGrow={1}>{children}</VStack>
      </HStack>
    </VStack>
  );
};

export default Layout;
